import React, { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import './dashboard.css';
import DashboardHome from './components/homedash';
import ArticlesPage from './components/Articals';
import CategoriesPage from './components/categoriesPage';
import Header from './components/dashboardHeader';
import Sidebar from './components/dashboardSidebar';
import AddNewsPage from './components/AddNewsPage';
import EditNewsPage from './components/UpdateNewsPage';
import SettingsAdmins from './components/settings';
import ReviewPage from './components/review';

const Dashboard = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [currentPage, setCurrentPage] = useState(1);
  const [newsCurrentPage, setNewsCurrentPage] = useState(1);
  const [isAddingNews, setIsAddingNews] = useState(false);
  const [articlesData, setArticlesData] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Edit state management
  const [editingArticle, setEditingArticle] = useState(null);
  const editRefreshCallbackRef = useRef(null);

  const itemsPerPage = 5;
  const latestNews = [];

  const newsTotalPages = Math.ceil(latestNews.length / itemsPerPage);
  const newsStartIndex = (newsCurrentPage - 1) * itemsPerPage;
  const paginatedNews = latestNews.slice(newsStartIndex, newsStartIndex + itemsPerPage);

  const articlesTotalPages = Math.ceil(articlesData.length / itemsPerPage);
  const articlesStartIndex = (currentPage - 1) * itemsPerPage;
  const paginatedArticles = articlesData.slice(articlesStartIndex, articlesStartIndex + itemsPerPage);

  // Close mobile menu when screen size changes to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  // Global query tracking - FIXED
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'observerAdded' || event.type === 'observerRemoved') {
   
      }
    });

    // Also track invalidations
    const originalInvalidate = queryClient.invalidateQueries;
    queryClient.invalidateQueries = function(...args) {
      
      console.trace('Invalidation called from:'); 
      return originalInvalidate.apply(this, args);
    };

    return () => {
      unsubscribe();
      queryClient.invalidateQueries = originalInvalidate; // Restore original
    };
  }, [queryClient]); // Added missing dependency

  const handleAddNews = () => {
    setIsAddingNews(true);
  };

  // Fixed handleSaveNews function
  const handleSaveNews = (newArticle) => {
  
    
    setArticlesData(prev => [newArticle, ...prev]);

    
    // Only invalidate the specific category when ADDING news
    if (newArticle.category_id) {
    
      queryClient.invalidateQueries(['articles', 'category', newArticle.category_id]);
    } else {
      console.warn('Dashboard: No category_id provided for new article');
      // Don't invalidate anything if no category_id
    }
    
  //    if (newArticle.category_id === "4") {
  //   setActiveTab('review');
  // } else {
  //   setActiveTab('articles');
  // }
    setIsAddingNews(false);
    setIsMobileMenuOpen(false);
  };

  // Handle edit article from any component
  const handleEditArticle = (article, refreshCallback) => {
 
    setEditingArticle(article);
    editRefreshCallbackRef.current = refreshCallback;
  };

  // Handle back from edit
  const handleBackFromEdit = () => {
  
    setEditingArticle(null);
    editRefreshCallbackRef.current = null;
  };

  // Handle article update completion
  const handleUpdateArticle = (updatedArticle) => {
  
    setEditingArticle(null);
    editRefreshCallbackRef.current = null;
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setIsAddingNews(false);
    setIsMobileMenuOpen(false); // Close mobile menu when tab changes
    // Clear edit state when changing tabs
    setEditingArticle(null);
    editRefreshCallbackRef.current = null;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const renderContent = () => {
    // Handle editing state first - takes priority over everything
    if (editingArticle) {
      return (
        <EditNewsPage 
          article={editingArticle}
          onBack={handleBackFromEdit}
          onUpdate={handleUpdateArticle}
          onRefresh={editRefreshCallbackRef.current}
        />
      );
    }

    // Handle add-news tab
    if (activeTab === 'add-news') {
      return <AddNewsPage onBack={() => setActiveTab('articles')} onSave={handleSaveNews} />;
    }

    // Handle articles tab
    if (activeTab === 'articles') {
      if (isAddingNews) {
        return <AddNewsPage onBack={() => setIsAddingNews(false)} onSave={handleSaveNews} />;
      } else {
        return (
          <ArticlesPage
            paginatedArticles={paginatedArticles}
            currentPage={currentPage}
            articlesTotalPages={articlesTotalPages}
            setCurrentPage={setCurrentPage}
            onAddNews={handleAddNews}
            onEditArticle={handleEditArticle}
          />
        );
      }
    }

    // Handle other tabs
    switch (activeTab) {
      case 'dashboard':
        return (
          <DashboardHome
            paginatedNews={paginatedNews}
            newsCurrentPage={newsCurrentPage}
            newsTotalPages={newsTotalPages}
            setNewsCurrentPage={setNewsCurrentPage}
          />
        );
      case 'categories':
        return <CategoriesPage />;
      case 'review':
        return (
          <ReviewPage 
            setActiveTab={handleTabChange} 
            onEditArticle={handleEditArticle}
          />
        );
      case 'settings':
        return <SettingsAdmins />
      default:
        return (
          <DashboardHome
            paginatedNews={paginatedNews}
            newsCurrentPage={newsCurrentPage}
            newsTotalPages={newsTotalPages}
            setNewsCurrentPage={setNewsCurrentPage}
          />
        );
    }
  };

  return (
    <div className="dashboard-container">
      <Header 
        toggleMobileMenu={toggleMobileMenu}
        isMobileMenuOpen={isMobileMenuOpen}
      />
      <div className="main-layout">
        <Sidebar 
          activeTab={activeTab} 
          setActiveTab={handleTabChange}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />
        <main className="main-content-dashboard">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;